import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Save, Upload, Search, Share2 } from 'lucide-react';

export const SEOManager = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image_url: '',
    og_type: 'website',
    twitter_card: 'summary_large_image',
    twitter_title: '',
    twitter_description: '',
    twitter_image_url: '',
    business_name: '',
    business_description: '',
    canonical_url: '',
    robots: 'index, follow',
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSEO();
  }, []);

  const fetchSEO = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/seo`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching SEO:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/seo`, formData, {
        headers: getAuthHeader(),
      });
      alert('SEO settings updated successfully!');
    } catch (error) {
      console.error('Error updating SEO:', error);
      alert('Failed to update SEO settings');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file, field) => {
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData({ ...formData, [field]: response.data.url });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload image');
    }
  };

  if (loading) {
    return <div className="text-white">Loading...</div>;
  }

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">SEO Settings</h2>
        <p className="text-gray-400">
          Optimize your site for search engines and social media sharing
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic SEO */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Search className="w-5 h-5 text-orange-500" />
            <h3 className="text-xl font-bold text-white">Basic SEO</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Meta Title (60 characters max)
              </label>
              <input
                type="text"
                maxLength="60"
                value={formData.meta_title}
                onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <p className="text-gray-400 text-xs mt-1">
                {formData.meta_title.length}/60 characters
              </p>
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Meta Description (160 characters max)
              </label>
              <textarea
                maxLength="160"
                value={formData.meta_description}
                onChange={(e) =>
                  setFormData({ ...formData, meta_description: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows="3"
              />
              <p className="text-gray-400 text-xs mt-1">
                {formData.meta_description.length}/160 characters
              </p>
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Meta Keywords (comma-separated)
              </label>
              <input
                type="text"
                value={formData.meta_keywords}
                onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  Canonical URL
                </label>
                <input
                  type="url"
                  value={formData.canonical_url}
                  onChange={(e) =>
                    setFormData({ ...formData, canonical_url: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  Robots
                </label>
                <select
                  value={formData.robots}
                  onChange={(e) => setFormData({ ...formData, robots: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="index, follow">Index, Follow</option>
                  <option value="noindex, nofollow">No Index, No Follow</option>
                  <option value="index, nofollow">Index, No Follow</option>
                  <option value="noindex, follow">No Index, Follow</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Open Graph */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Share2 className="w-5 h-5 text-blue-500" />
            <h3 className="text-xl font-bold text-white">Open Graph (Facebook, LinkedIn)</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                OG Title
              </label>
              <input
                type="text"
                value={formData.og_title}
                onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                OG Description
              </label>
              <textarea
                value={formData.og_description}
                onChange={(e) =>
                  setFormData({ ...formData, og_description: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows="2"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                OG Image (1200x630px recommended)
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={formData.og_image_url}
                  onChange={(e) =>
                    setFormData({ ...formData, og_image_url: e.target.value })
                  }
                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <label className="cursor-pointer">
                  <div className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                    <Upload className="w-5 h-5 mr-2" />
                    Upload
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleImageUpload(e.target.files[0], 'og_image_url');
                      }
                    }}
                    className="hidden"
                  />
                </label>
              </div>
              {formData.og_image_url && (
                <img
                  src={formData.og_image_url}
                  alt="OG preview"
                  className="mt-3 h-32 object-cover bg-gray-900 rounded"
                />
              )}
            </div>
          </div>
        </div>

        {/* Twitter Card */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Share2 className="w-5 h-5 text-sky-500" />
            <h3 className="text-xl font-bold text-white">Twitter Card</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Twitter Title
              </label>
              <input
                type="text"
                value={formData.twitter_title}
                onChange={(e) =>
                  setFormData({ ...formData, twitter_title: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Twitter Description
              </label>
              <textarea
                value={formData.twitter_description}
                onChange={(e) =>
                  setFormData({ ...formData, twitter_description: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows="2"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Twitter Image
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={formData.twitter_image_url}
                  onChange={(e) =>
                    setFormData({ ...formData, twitter_image_url: e.target.value })
                  }
                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <label className="cursor-pointer">
                  <div className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                    <Upload className="w-5 h-5 mr-2" />
                    Upload
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleImageUpload(e.target.files[0], 'twitter_image_url');
                      }
                    }}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Business Info */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-4">Business Information</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Business Name
              </label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) =>
                  setFormData({ ...formData, business_name: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Business Description
              </label>
              <textarea
                value={formData.business_description}
                onChange={(e) =>
                  setFormData({ ...formData, business_description: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows="3"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="flex items-center px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save SEO Settings'}
        </button>
      </form>
    </div>
  );
};
